import React, { useState } from "react";
import {
  ShieldCheck,
  KeyRound,
  Landmark,
  Fingerprint,
  Eye,
  Download,
  MoreHorizontal,
  Plus,
} from "lucide-react";
import "./Certificates.css";

const TABS = ["My Certificates", "Trusted Certificates", "Revoked Certificates"];

const certificates = [
  {
    id: 1,
    name: "Demo Admin",
    email: "admin@intrust.com",
    type: "Digital Certificate (PQ)",
    typeClass: "pq",
    issuer: "Intrust Quantum CA",
    validFrom: "May 18, 2026",
    validTo: "May 18, 2028",
    status: "Active",
    icon: "shield",
    iconColor: "purple",
    details: {
      subtype: "Digital Certificate (PQ)",
      serialNumber: "PQ-25-05-18-0001",
      algorithm: "Dilithium3 (PQC)",
      issuer: "Intrust Quantum CA",
      subject: "CN=Demo Admin, O=Intrust Technologies, C=IN",
      validFrom: "May 18, 2026 10:30 AM (IST)",
      validTo: "May 18, 2028 10:30 AM (IST)",
    },
  },
  {
    id: 2,
    name: "Demo Admin (Hybrid)",
    email: "admin.hybrid@intrust.com",
    type: "Hybrid Certificate",
    typeClass: "hybrid",
    issuer: "Intrust Hybrid CA",
    validFrom: "May 05, 2026",
    validTo: "May 05, 2028",
    status: "Active",
    icon: "key",
    iconColor: "violet",
    details: {
      subtype: "Hybrid Certificate (PQ + Classical)",
      serialNumber: "HY-25-05-05-0002",
      algorithm: "Falcon512 + ECDSA P-256",
      issuer: "Intrust Hybrid CA",
      subject: "CN=Demo Admin, O=Intrust Technologies, C=IN",
      validFrom: "May 05, 2026 09:00 AM (IST)",
      validTo: "May 05, 2028 09:00 AM (IST)",
    },
  },
  {
    id: 3,
    name: "Demo Admin (Classical)",
    email: "admin.classical@intrust.com",
    type: "Classical Certificate",
    typeClass: "classical",
    issuer: "Intrust CA",
    validFrom: "Apr 10, 2025",
    validTo: "Apr 10, 2027",
    status: "Active",
    icon: "landmark",
    iconColor: "gray",
    details: {
      subtype: "Classical Certificate",
      serialNumber: "CL-24-04-10-0003",
      algorithm: "RSA-2048",
      issuer: "Intrust CA",
      subject: "CN=Demo Admin, O=Intrust Technologies, C=IN",
      validFrom: "Apr 10, 2025 11:15 AM (IST)",
      validTo: "Apr 10, 2027 11:15 AM (IST)",
    },
  },
  {
    id: 4,
    name: "Hardware Identity (YubiKey)",
    email: "admin.yubikey@intrust.com",
    type: "Hardware Certificate",
    typeClass: "hardware",
    issuer: "Intrust Hardware CA",
    validFrom: "May 18, 2026",
    validTo: "May 18, 2028",
    status: "Active",
    icon: "fingerprint",
    iconColor: "blue",
    details: {
      subtype: "Hardware Security Module",
      serialNumber: "HW-25-05-18-0004",
      algorithm: "ECDSA P-384 (HSM)",
      issuer: "Intrust Hardware CA",
      subject: "CN=Demo Admin, O=Intrust Technologies, C=IN",
      validFrom: "May 18, 2026 02:45 PM (IST)",
      validTo: "May 18, 2028 02:45 PM (IST)",
    },
  },
];

function CertIcon({ type, size = 18 }) {
  switch (type) {
    case "key":
      return <KeyRound size={size} strokeWidth={2} />;
    case "landmark":
      return <Landmark size={size} strokeWidth={2} />;
    case "fingerprint":
      return <Fingerprint size={size} strokeWidth={2} />;
    case "shield":
    default:
      return <ShieldCheck size={size} strokeWidth={2} />;
  }
}

export default function Certificates() {
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [selectedId, setSelectedId] = useState(certificates[0].id);

  const selected = certificates.find((c) => c.id === selectedId) || certificates[0];

  return (
    <div className="cert-page">
      <div className="cert-container">
        <div className="cert-main-card">

          {/* Top bar: tabs + import button */}
          <div className="cert-topbar">
            <div className="cert-tabs">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  className={`cert-tab${activeTab === tab ? " active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
            <button className="cert-import-btn">
              <Plus size={15} strokeWidth={2.5} />
              <span>Import Certificate</span>
            </button>
          </div>

          <div className="cert-card-divider" />

          {/* Table */}
          <div className="cert-table-scroll">
            <table className="cert-table">
              <thead>
                <tr>
                  <th>Certificate</th>
                  <th>Type</th>
                  <th>Issuer</th>
                  <th>Valid From</th>
                  <th>Valid To</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {certificates.map((cert) => (
                  <tr
                    key={cert.id}
                    onClick={() => setSelectedId(cert.id)}
                    className={selectedId === cert.id ? "cert-row-selected" : ""}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <div className="cert-name-cell">
                        <div className={`cert-icon-wrap ${cert.iconColor}`}>
                          <CertIcon type={cert.icon} />
                        </div>
                        <div>
                          <p className="cert-holder-name">{cert.name}</p>
                          <p className="cert-holder-email">{cert.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`cert-type ${cert.typeClass}`}>
                        {cert.type}
                      </span>
                    </td>
                    <td className="cert-issuer">{cert.issuer}</td>
                    <td className="cert-date">{cert.validFrom}</td>
                    <td className="cert-date">{cert.validTo}</td>
                    <td>
                      <span className="cert-status-badge">
                        <span className="cert-status-dot" />
                        {cert.status}
                      </span>
                    </td>
                    <td>
                      <div className="cert-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="cert-action-btn" aria-label="View">
                          <Eye size={15} strokeWidth={2} />
                        </button>
                        <button className="cert-action-btn" aria-label="Download">
                          <Download size={15} strokeWidth={2} />
                        </button>
                        <button className="cert-action-btn" aria-label="More options">
                          <MoreHorizontal size={15} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cert-card-divider" />

        </div>

        {/* Certificate Details — separate card */}
        <div className="cert-main-card cert-details-card">
          <div className="cert-details-inner">
            <h3 className="cert-details-title">Certificate Details</h3>

            <div className="cert-details-top">
              <div className="cert-details-icon">
                <CertIcon type={selected.icon} size={22} />
              </div>
              <div>
                <p className="cert-details-name">{selected.name}</p>
                <p className="cert-details-subtype">{selected.details.subtype}</p>
              </div>
            </div>

            <div className="cert-details-grid">
              <div>
                <p className="cert-field-label">Serial Number</p>
                <p className="cert-field-value mono">{selected.details.serialNumber}</p>
              </div>
              <div>
                <p className="cert-field-label">Issuer</p>
                <p className="cert-field-value">{selected.details.issuer}</p>
              </div>
              <div>
                <p className="cert-field-label">Valid From</p>
                <p className="cert-field-value">{selected.details.validFrom}</p>
              </div>
              <div>
                <p className="cert-field-label">Algorithm</p>
                <p className="cert-field-value">{selected.details.algorithm}</p>
              </div>
              <div>
                <p className="cert-field-label">Subject</p>
                <p className="cert-field-value">{selected.details.subject}</p>
              </div>
              <div>
                <p className="cert-field-label">Valid To</p>
                <p className="cert-field-value">{selected.details.validTo}</p>
              </div>
            </div>

            <div className="cert-details-actions">
              <button className="cert-btn">
                <Eye size={15} strokeWidth={2} />
                View Certificate
              </button>
              <button className="cert-btn">
                <Download size={15} strokeWidth={2} />
                Download
              </button>
              <button className="cert-btn">Export</button>
              <button className="cert-btn danger">Revoke Certificate</button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}